import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  AboutUs,
  HeroSection,
  IncubationPartners,
  ScrollNavbar,
  SearchSection,
} from "../../components";
import Features from "../../components/Landing/Features";
import Banner from "../../components/Landing/Banner";
import Footer from "../../components/Footer/Footer";
import Services from "../../components/Landing/Services";
import searchService, { City } from "../../services/searchService";
import ComingSoon from "../../components/Landing/ComingSoon";
import SearchSectionNew from "../../components/Landing/SearchSectionNew/SearchSectionNew";
import PhoneOtpModal from "../../components/Auth/PhoneOtpModal";
import { useAuth } from "../../contexts/AuthContext";
import { PlaceSelection } from "../../components/PlaceAutocompleteInput/PlaceAutocompleteInput";
import { findNearestCity } from "../../utils/geo";
import {
  VerticalType,
  TripType,
  VERTICALS,
  TRIP_TYPES,
  getHourlyDurationKey,
  isSourceFieldVisible,
  isDestinationFieldVisible,
  isReturnLegRequired,
} from "../../constants/tripTypes";

const Landing = () => {
  const history = useHistory();
  const { isAuthenticated } = useAuth();
  const [showOtpModal, setShowOtpModal] = useState(false);

  // Trip vertical state
  const [activeTab, setActiveTab] = useState<VerticalType>(VERTICALS.INTERCITY);

  const [tripType, setTripType] = useState<TripType>(TRIP_TYPES.ONEWAY);

  // Form data states
  const [source, setSource] = useState<City | null>(null);
  const [destination, setDestination] = useState<City | null>(null);
  const [departureDate, setDepartureDate] = useState<string>("");
  const [returnDate, setReturnDate] = useState<string>("");
  const [pickupTime, setPickupTime] = useState<string>("");
  const [dropTime, setDropTime] = useState<string>("");
  const [sourceSearch, setSourceSearch] = useState<string>("");
  const [destSearch, setDestSearch] = useState<string>("");

  // Exact pickup/drop address captured via Google Places Autocomplete - see
  // matching state in Dashboard.tsx. `source`/`destination` (City) is still
  // derived from these (nearest match) since Price/Plan routes are
  // configured per city pair, not exact address.
  const [sourceAddress, setSourceAddress] = useState<PlaceSelection | null>(
    null
  );
  const [destinationAddress, setDestinationAddress] =
    useState<PlaceSelection | null>(null);
  const [allCities, setAllCities] = useState<City[]>([]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    searchService
      .getAllCities()
      .then((response) => {
        if (response.success && response.data) setAllCities(response.data);
      })
      .catch((error) => console.error("Failed to load cities list:", error));
  }, []);

  const handleSourcePlaceSelected = (place: PlaceSelection) => {
    setSourceSearch(place.address);
    setSourceAddress(place);
    setSource(findNearestCity(allCities, place.lat, place.lng));
  };

  const handleDestinationPlaceSelected = (place: PlaceSelection) => {
    setDestSearch(place.address);
    setDestinationAddress(place);
    setDestination(findNearestCity(allCities, place.lat, place.lng));
  };

  // Airport-side city pick (no Google Places involved) - the City's own
  // lat/long is used as-is, so no address override.
  const handleSourceCitySelected = (city: City) => {
    setSourceSearch(city.name);
    setSourceAddress(null);
    setSource(city);
  };

  const handleDestinationCitySelected = (city: City) => {
    setDestSearch(city.name);
    setDestinationAddress(null);
    setDestination(city);
  };

  const handleSwapLocations = () => {
    const tempSource = source;
    const tempSourceSearch = sourceSearch;
    const tempSourceAddress = sourceAddress;

    setSource(destination);
    setSourceSearch(destSearch);
    setSourceAddress(destinationAddress);

    setDestination(tempSource);
    setDestSearch(tempSourceSearch);
    setDestinationAddress(tempSourceAddress);
  };

  const proceedToSearch = () => {
    const params = new URLSearchParams();

    // Add search parameters
    params.set("vertical", activeTab);
    params.set("trip-type", tripType);
    if (source) params.set("source", source.name);
    if (destination) params.set("destination", destination.name);
    if (departureDate) params.set("departure-date", departureDate);
    if (returnDate) params.set("return-date", returnDate);
    if (pickupTime) params.set("pickup-time", pickupTime);
    if (dropTime) params.set("drop-time", dropTime);
    if (activeTab === VERTICALS.HOURLY) {
      params.set("hourly-duration", getHourlyDurationKey(tripType) ?? "4");
    }

    // Redirect to dashboard with search params - exact address (if
    // captured) rides along as location.state, since it's too precise/long
    // for a query param and Dashboard.tsx's initial-load effect reads it
    // from there (see navState).
    history.push({
      pathname: "/dashboard",
      search: params.toString(),
      state: {
        sourceAddress: sourceAddress ?? undefined,
        destinationAddress: destinationAddress ?? undefined,
      },
    });
  };

  const handleSearch = () => {
    if (!isAuthenticated) {
      setShowOtpModal(true);
      return;
    }
    proceedToSearch();
  };

  const handleClearFilter = () => {
    // Reset form states but keep the active tab
    setSource(null);
    setDestination(null);
    setSourceSearch("");
    setDestSearch("");
    setSourceAddress(null);
    setDestinationAddress(null);
    setDepartureDate("");
    setReturnDate("");
    setPickupTime("");
    setDropTime("");
  };

  const isSearchEnabled = (() => {
    if (!departureDate || !pickupTime) {
      return false;
    }

    if (isSourceFieldVisible(activeTab, tripType) && !source) {
      return false;
    }

    if (isDestinationFieldVisible(activeTab, tripType) && !destination) {
      return false;
    }

    if (isReturnLegRequired(tripType) && (!returnDate || !dropTime)) {
      return false;
    }

    return true;
  })();

  const showClearButton = !!(
    source ||
    destination ||
    departureDate ||
    returnDate ||
    pickupTime ||
    dropTime ||
    sourceSearch ||
    destSearch
  );

  return (
    <>
      <ScrollNavbar />
      <div
        className="px-4 sm:px-8 lg:px-12"
        style={{ maxWidth: "1400px", margin: "0 auto" }}
      >
        <section className="mb-20">
          <HeroSection />
        </section>

        <section className="my-20 relative z-50">
          {/* <ComingSoon /> */}
          <SearchSectionNew
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tripType={tripType}
            setTripType={setTripType}
            source={source}
            setSource={setSource}
            destination={destination}
            setDestination={setDestination}
            departureDate={departureDate}
            setDepartureDate={setDepartureDate}
            returnDate={returnDate}
            setReturnDate={setReturnDate}
            pickupTime={pickupTime}
            setPickupTime={setPickupTime}
            dropTime={dropTime}
            setDropTime={setDropTime}
            sourceSearch={sourceSearch}
            setSourceSearch={setSourceSearch}
            destSearch={destSearch}
            setDestSearch={setDestSearch}
            onSourcePlaceSelected={handleSourcePlaceSelected}
            onDestinationPlaceSelected={handleDestinationPlaceSelected}
            allCities={allCities}
            onSourceCitySelected={handleSourceCitySelected}
            onDestinationCitySelected={handleDestinationCitySelected}
            onSwapLocations={handleSwapLocations}
            handleSearch={handleSearch}
            handleClearFilter={handleClearFilter}
            isSearchEnabled={isSearchEnabled}
            showClearButton={showClearButton}
          />
          {/* <h2 className="text-center text-lg font-medium">Launching Soon!</h2> */}
        </section>

        <section className="my-20">
          <AboutUs />
        </section>

        <section className="my-20">
          <Services />
        </section>

        <section className="my-20">
          <Features />
        </section>

        <section className="my-20">
          <IncubationPartners />
        </section>

        <section>
          <Banner />
        </section>
      </div>
      <Footer />
      <PhoneOtpModal
        open={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onVerified={() => {
          setShowOtpModal(false);
          proceedToSearch();
        }}
      />
    </>
  );
};

export default Landing;
